import { useState } from 'react';
import * as Accordion from '@radix-ui/react-accordion';
import {
  ComponentRendering,
  Field,
  GetStaticComponentProps,
  LayoutServiceData,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { toLower } from 'lodash';

import { icon_plus } from '@gn-audio/icons';
import { Button, isColorBright, resolveColorProperty } from '@gn-audio/jss-components';
import { JabraIcon } from '@gn-audio/jss-components';
import tailwindConfig from '@gn-audio/jss-components/tailwind-presets/jabra-preset';

import { mapSharedPropertiesToFields } from 'common/getters';
import { getSharedDictionary } from 'common/getters/getSharedDictionary';
import { useJabraThemesList } from 'common/jabraTheme/JabraThemesListContext';
import { withTheme } from 'common/jabraTheme/withTheme';
import { ComponentProps } from 'lib/component-props';
import useFallbackTranslation from 'utils/hooks/useFallbackTranslation';

type FAQSpotProps = {
  title: string;
  faqItems: FAQItem[];
  currentComponentTheme?: string;
} & ComponentProps;

type FAQSpotItem = {
  id: Field<string>;
  question: Field<string>;
  questionShared: Field<string>;
  answerShared: Field<string>;
  answer: Field<string>;
};

type FAQSpotRendering = {
  fields: {
    faqQuestions: {
      id: string;
      fields: FAQSpotItem;
    }[];
    faqQuestionsShared: {
      id: string;
      fields: FAQSpotItem;
    }[];
    title: Field<string>;
    titleShared: Field<string>;
  };
} & ComponentRendering;

type FAQItem = {
  id: string;
  question: string;
  answer: string;
};

const FAQSpot = ({ faqItems, currentComponentTheme, title }: FAQSpotProps) => {
  const { t } = useFallbackTranslation();
  const handleShowMore = () => {
    setIsShowMoreButtonVisible(false);
  };

  const allThemes = useJabraThemesList();
  const currentTheme = allThemes.find(
    (themeItem) => toLower(themeItem.name) === toLower(currentComponentTheme)
  );
  const themeBgColor = currentTheme?.backgroundColor?.value;
  const bgColor = !!themeBgColor ? resolveColorProperty(themeBgColor) : undefined;
  const bgContrast = isColorBright(bgColor) ? 'black' : 'white';

  const textColorClass = bgContrast === 'black' ? 'text-black' : 'text-white';
  const buttonColorClass = bgContrast === 'black' ? '' : 'dark';

  const limitedFaqs = faqItems.slice(0, 10);
  const initialState = limitedFaqs.length < faqItems.length;
  const [isShowMoreButtonVisible, setIsShowMoreButtonVisible] = useState(initialState);

  const faqs = isShowMoreButtonVisible ? limitedFaqs : faqItems;
  return (
    <>
      <div className="bg-theme">
        <div className="container flex flex-col gap-12">
          <header>
            <h2 className="text-theme text-5xl font-bold leading-[43.78x]">{title}</h2>
          </header>

          <Accordion.Root
            orientation="horizontal"
            type="single"
            collapsible
            className="w-full text-left"
          >
            {faqs.map((faq, index) => (
              <Accordion.Item
                className="flex w-full flex-col overflow-hidden border-b-[1px] border-solid py-[18px]"
                key={faq.id}
                value={`item-${index}`}
              >
                <section>
                  <Accordion.Trigger className="group flex w-full items-center justify-between">
                    <div className={`text-left ${textColorClass} font-normal leading-[21.89px]`}>
                      {faq.question}
                    </div>
                    <JabraIcon
                      iconStyle={{ strokeWidth: 2 }}
                      className={`h-5 min-h-5 w-5 min-w-5 group-data-[state=open]:rotate-45 ${textColorClass} `}
                      Icon={icon_plus}
                    />
                  </Accordion.Trigger>
                </section>
                <Accordion.Content className="collapsible-content overflow-hidden">
                  <p
                    className={`pt-5 text-base ${textColorClass} font-light leading-6`}
                    dangerouslySetInnerHTML={{ __html: faq.answer }}
                  ></p>
                </Accordion.Content>
              </Accordion.Item>
            ))}
          </Accordion.Root>
          {isShowMoreButtonVisible && (
            <section className={`${buttonColorClass} flex`}>
              <Button
                aria-label={`${t('system.ShowMoreFAQ')}`}
                size="m"
                variant="secondary"
                onClick={handleShowMore}
              >
                {t('system.ShowMoreFAQ') || 'SHOW MORE FAQ'}
              </Button>
            </section>
          )}
        </div>
      </div>
    </>
  );
};

export const getStaticProps: GetStaticComponentProps = async (
  rendering: FAQSpotRendering,
  layout: LayoutServiceData
) => {
  const fields = rendering.fields;

  const title = getSharedDictionary(fields, 'title')?.value ?? '';

  const faqQuestionsFields =
    fields.faqQuestions.length > 0 ? fields.faqQuestions : fields.faqQuestionsShared;

  const faqQuestions = faqQuestionsFields.filter(
    (faqQuestion) => Object.keys(faqQuestion.fields).length > 0
  );

  const faqItems = faqQuestions.map((faq) => {
    const { answer, question } = mapSharedPropertiesToFields<FAQSpotItem>(faq.fields);
    return { id: faq.id, answer: answer.value, question: question.value };
  }) as FAQItem[];

  const themeFromLayout = layout.sitecore.route?.fields?.Theme as Field<string>;
  const theme = rendering?.params?.Theme ?? themeFromLayout.value ?? null;
  return { faqItems, currentComponentTheme: theme, title };
};

export default withTheme({
  defaultTheme: {
    darkTheme: false,
    backgroundColor: tailwindConfig.theme.colors.white,
  },
})(withDatasourceCheck()(FAQSpot));
